
import { API_BASE_URL } from "../../config";
import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel
} from "@/components/ui/form"
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Dropdown } from '../ui/filterSelect';
import { TagBox } from "../ui/tagbox";
import { Checkbox } from "@/components/ui/checkbox";
import { Popup } from "@/components/common/Popup";
import { AgendaItemInfo } from "@/components/agenda/AgendaItemInfo";


import { Trash2 } from "lucide-react";
import { useApiFetch } from "@/helper/apiFetch";
import { PresenterConnectionSchema, PresenterMainSchema, PresenterSchema, TextVariableSchema } from "./PresenterSchema";
import { PlenumInfoSchema } from "@/components/plenum/PlenumSchema";
import { Toggle } from "../ui/toggle";
import { NumberInput } from "../ui/number";
import { TextArea } from "../ui/textarea";
type PresenterInfoProps = {
  userId?: string;
  eventId: string | null;
  onClose: (agenda: z.infer<typeof PresenterMainSchema> | null) => void;
};



type Lookup = { Id: string; Description: string };
type ConType = { ConnTypeId: number; type_name: string };
type PropType = { id: number; prop_id: number; select_value: string };
type TextVariableLanguageKey = Exclude<keyof z.infer<typeof TextVariableSchema>, "string_key" | "name">;
type PresenterNameFieldPath = `InfoTexts.${number}.${TextVariableLanguageKey}`;

const textVariableLanguageKeys: TextVariableLanguageKey[] = ["de", "en", "fr", "es", "pt", "cz", "zh"];

function isTextVariableLanguageKey(value: string): value is TextVariableLanguageKey {
  return textVariableLanguageKeys.includes(value as TextVariableLanguageKey);
}

function parsePlenums(value?: string | null): number[] {
  if (!value) {
    return [];
  }

  return value
    .split(",")
    .map((item) => Number(item.trim()))
    .filter((item) => !Number.isNaN(item));
}






export function PresenterInfo({ userId, eventId, onClose }: PresenterInfoProps) {
  const apiFetch = useApiFetch();
  const [isLoaded, setIsLoaded] = useState(false);
  const [avatarRefreshToken, setAvatarRefreshToken] = useState(Date.now());
  const [plenums, setPlenums] = useState<PlenumInfoSchema[]>([]);
  const [presenterTypes, setPresenterTypes] = useState<ConType[]>([]);
  const [clickerTypes, setClickerTypes] = useState<ConType[]>([]);
  const [errorText, setError] = useState("");
  const [tabIndex, setIndex] = useState(-1);
  const [moduleIndex, setModuleIndex] = useState(-1);
  const [clickerIndex, setClickerIndex] = useState(-1);
  const [propertySourceVersion, setPropertySourceVersion] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const propertySourceCacheRef = useRef<Record<number, PropType[]>>({});
  const loadingPropertySourceIdsRef = useRef<Set<number>>(new Set());
  const languages: { key: TextVariableLanguageKey; name: string }[] = [{ key: "de", name: "German" }, { key: "en", name: "English" }, { key: "fr", name: "French" }, { key: "es", name: "Spanish" }, { key: "pt", name: "Portuguese" }, { key: "cz", name: "Czech" }, { key: "zh", name: "Chinese" }];
  const [languageTab, setLanguageTab] = useState<TextVariableLanguageKey>(languages[0]?.key ?? "de");
  const activeLanguageKey: TextVariableLanguageKey = isTextVariableLanguageKey(languageTab) ? languageTab : "de";

  const form = useForm<z.infer<typeof PresenterMainSchema>>({
    resolver: zodResolver(PresenterMainSchema),
    defaultValues: {
      PresenterSettings: [{ EventNr: eventId }]
    }
  });

  const infoTexts = form.watch("InfoTexts") ?? [];
  const selectedPlenums = form.watch("Plenums") ?? [];
  const presenterNameIndex = infoTexts.findIndex((item) => item?.string_key === form.getValues("UserInfo.Title_Key"));
  const presenterDescIndex = infoTexts.findIndex((item) => item?.string_key === form.getValues("UserInfo.Description_Key"));



  useEffect(() => {
    const properties = form.getValues(`PresenterSettings.${tabIndex}`);
    console.log("Module properties updated:", properties);
  }, [tabIndex, moduleIndex, form]);


  const selectPlenum = async (tab: number) => {
    //check we have a record
    const presenterSettings = form.getValues("PresenterSettings") ?? [];
    if (presenterSettings.filter(s => s.PlenumId == tab).length === 0) {

      const res = await apiFetch(`/Admin/CreatePlenumPresenter?eventId=${eventId}&userId=${userId}&plenumId=${tab}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });

      if (!res.ok) {
        const e = await res.text();
        setError(e);

        return;
      }

      const temp = await res.json();

      presenterSettings.push(temp as z.infer<typeof PresenterSchema>);
      form.setValue("PresenterSettings", presenterSettings);

    }
    //find index in presenter settings
    const index = presenterSettings.findIndex(s => s.PlenumId == tab);
    setIndex(index);

    const module = presenterSettings[index].PresenterConnections?.find(t => t.section_id == 2 && t.active);
    console.log('activemodule', module);
    if (module) {
      const connTypeId = module.ConnTypeId ?? 0;
      loadModuleSettings(connTypeId, 2, index);
    }
    const clicker = presenterSettings[index].PresenterConnections?.find(t => t.section_id == 3 && t.active);


    if (clicker) {
      const connTypeId = clicker.ConnTypeId ?? 0;
      loadModuleSettings(connTypeId, 3, index);
    }
  }


  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setError("");
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";

    if (!eventId) {
      setError("Missing event id");
      return;
    }

    if (file.size > 20 * 1024 * 1024) {
      setError("File size exceeds 20MB limit");
      return;
    }

    setIsUploadingFile(true);
    try {
      const fd = new FormData();
      fd.append("myFile", file);

      let res = await apiFetch(`/Admin/UploadFile?ownerId=${userId}`, { method: "POST", body: fd });


      if (!res.ok) {
        const eText = await res.text();
        setError(eText || "File upload failed");
        return;
      }

      // Cache-bust image URL so browser fetches the new avatar immediately.
      setAvatarRefreshToken(Date.now());

    } catch (err) {
      console.error(err);
      setError("File upload failed");
    } finally {
      setIsUploadingFile(false);
    }
  };

  const getPropertySource = (propId: number): PropType[] => {
    // Read dependency so the component rerenders when async data lands.
    void propertySourceVersion;

    if (!propId) {
      return [];
    }

    const cached = propertySourceCacheRef.current[propId];
    if (cached) {
      return cached;
    }

    if (!loadingPropertySourceIdsRef.current.has(propId)) {
      loadingPropertySourceIdsRef.current.add(propId);
      apiFetch(`/api/EventPool/GetSelectValues?propId=${propId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      })
        .then(async (res) => {
          if (!res.ok) {
            const e = await res.text();
            setError(e);
            return;
          }

          const data = await res.json();
          propertySourceCacheRef.current[propId] = (data?.data ?? []) as PropType[];
          setPropertySourceVersion((value) => value + 1);
        })
        .catch((err) => {
          console.error(err);
        })
        .finally(() => {
          loadingPropertySourceIdsRef.current.delete(propId);
        });
    }

    return [];
  }
  const loadModuleSettings = async (connTypeId: number, sectionId: number, settingsIndex: number = tabIndex) => {
    if (settingsIndex < 0) return;
    console.log(`Loading module settings for connTypeId=${connTypeId}, sectionId=${sectionId}, settingsIndex=${settingsIndex}`);
    const ownerId = form.getValues(`PresenterSettings.${settingsIndex}.Id` as const) ?? "";
    //see if it already exists
    const connections = form.getValues(`PresenterSettings.${settingsIndex}.PresenterConnections`) ?? [];
    const index = connections.findIndex(c => c.section_id == sectionId && c.ConnTypeId == connTypeId);
    const activeIndex = connections.findIndex(c => c.section_id == sectionId && c.active);

    //const pConnId = form.getValues(`PresenterSettings.${tabIndex}.PresenterConnections.${activeConnectionIndex}.Id` as const) ?? "";

    const res = await apiFetch(`/Admin/ValidateConnType?connType=${connTypeId}&ownerId=${ownerId}&sectionId=${sectionId}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (!res.ok) {
      const e = await res.text();
      setError(e);

      return;
    }

    const temp = await res.text();
    if (temp == "false") {
      setError("Selected module type is not valid for this presenter. Please select another one.");
      return;
    }


    if (sectionId == 2)
      form.setValue(`PresenterSettings.${settingsIndex}.SelectedModuleId`, connTypeId);
    else
      form.setValue(`PresenterSettings.${settingsIndex}.SelectedClickerId`, connTypeId);



    const res2 = await apiFetch(`/Admin/GetConnProps?connType=${connTypeId}&ownerId=${ownerId}&sectionId=${sectionId}&pConnId=0&newCode=true`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    });

    if (res2.ok) {
      const data = await res2.json();
      const presenterSettings = form.getValues("PresenterSettings") ?? [];

      if (activeIndex >= 0) {
        const priorConnection = presenterSettings[settingsIndex]?.PresenterConnections?.[activeIndex];
        if (priorConnection) {
          priorConnection.active = false;
        }
        //form.setValue("PresenterSettings", presenterSettings);

      }
      if (index >= 0) {
        const existingConnection = presenterSettings[settingsIndex]?.PresenterConnections?.[index];
        if (existingConnection) {
          existingConnection.active = true;
          existingConnection.PresenterConnProperties = data ?? [];
          form.setValue("PresenterSettings", presenterSettings);
        }
        console.log("Existing connection after update", presenterSettings, index, moduleIndex);

        if (sectionId === 3) {
          console.log('set clicker index', index)
          setClickerIndex(index);
        } else {
          setModuleIndex(index);
        }
        return;
      }

      console.log('add new connection with properties', data);

      const presenterConnectionNew = { Id: 0, ConnTypeId: connTypeId, section_id: sectionId, active: true, PresenterConnProperties: data ?? [], TOM: new Date() } as z.infer<typeof PresenterConnectionSchema>;

      const currentConnections = presenterSettings[settingsIndex].PresenterConnections ?? [];
      const updatedConnections = [...currentConnections, presenterConnectionNew];
      presenterSettings[settingsIndex].PresenterConnections = updatedConnections;


      form.setValue("PresenterSettings", presenterSettings);

      const newConnectionIndex = updatedConnections.length - 1;
      console.log("New connection index", newConnectionIndex, presenterSettings[settingsIndex].PresenterConnections[newConnectionIndex]);
      if (sectionId === 3)
      {
        setClickerIndex(newConnectionIndex);
      }
      else
        setModuleIndex(newConnectionIndex);

    }


  }

  const onSubmit = async (values: z.infer<typeof PresenterMainSchema>, event?: React.BaseSyntheticEvent) => {
    console.log("Presenter form submit intercepted by RHF", {
      defaultPrevented: event?.defaultPrevented,
      type: event?.type,
    });
    Save({ ...values, Plenums: parsePlenums(values.PlenumsAsString) }, true);
  }

  const Save = async (values: z.infer<typeof PresenterMainSchema>, closeAfterSave: boolean) => {
    try {
      setError("");

      // const buildFormData = (formData: FormData, data: any, prefix = "presenter") => {
      //   formData.append("eventId", String(eventId));
      //   Object.keys(data).forEach(key => {
      //     const value = data[key];
      //     const keyPath = prefix ? `${prefix}[${key}]` : key;

      //     if (value === null || value === undefined) {
      //       formData.append(keyPath, "");
      //     } else if (Array.isArray(value)) {
      //       value.forEach((item, index) => {
      //         const arrayPath = `${keyPath}[${index}]`;
      //         if (typeof item === "object" && item !== null) {
      //           buildFormData(formData, item, arrayPath);
      //         } else {
      //           formData.append(arrayPath, String(item ?? ""));
      //         }
      //       });
      //     } else if (typeof value === "object") {
      //       buildFormData(formData, value, keyPath);
      //     } else {
      //       formData.append(keyPath, String(value));
      //     }
      //   });
      // };

      // const formData = new FormData();
      // buildFormData(formData, values);

      const res = await apiFetch(`/Admin/SavePresenterNew?eventId=${eventId}`, {
        method: "POST",
        body: JSON.stringify(values),
      });

      if (!res.ok) {
        const e = await res.text();
        setError(e);

      }
      else {



        if (closeAfterSave) {
          onClose(values);
        }
        else {
          const temp = await res.text();
          userId = temp;
          form.setValue("UserId", userId);
        }

      }
    } catch (err) {
      console.error(err);
    }
  }

  useEffect(() => {


    apiFetch(`/Admin/_PresenterEdit?eventId=${eventId}&userId=${userId}&newCode=true`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch agenda group");
        return res.json();
      })
      .then((data) => {



        form.reset({
          ...data,
          PlenumsAsString: data.Plenums?.join(", "),
        });
        setIsLoaded(true);
        if (data.Plenums?.length > 0)
          selectPlenum(data.Plenums[0]);
      })
      .catch((err) => {
        console.error(err);
      });



  }, []);

  useEffect(() => {





    apiFetch(`/api/EventPool/GetPlenumListByEvent?isMarketPlace=false&eventId=${eventId}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch plenum list");
        return res.json();
      })
      .then((data) => {

        setPlenums(data.data);
      })
      .catch((err) => {
        console.error(err);
      });



    apiFetch(`/api/EventPool/GetSectionConnTypes?typePresenter=true`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to con types");
        return res.json();
      })
      .then((data) => {

        setPresenterTypes(data.data);
      })
      .catch((err) => {
        console.error(err);
      });



    apiFetch(`/api/EventPool/GetSectionConnTypes?inFrame=false`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to fetch con types");
        return res.json();
      })
      .then((data) => {

        setClickerTypes(data.data);
      })
      .catch((err) => {
        console.error(err);
      });



  }, [isLoaded]);



  return (
    <div className="p-5">
      {!isLoaded ? (
        <div>Loading…</div>
      ) : (
        <div>
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handleFileChange}
          />
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit, (error) => { console.log("Validation errors:", error, form.getValues()); })} className="space-y-2">


               <FormField
                  control={form.control}
                  name="PlenumsAsString"
                  render={({ field }) => (
                    <FormItem className="">
                      <FormLabel>Plenums</FormLabel>
                      <FormControl>
                        <TagBox
                          source={plenums}
                          value={field.value}
                          getLabel={(x) => x.Name ?? ""}
                          getValue={(x) => x.Id ?? 0}
                          onChange={(value) => {
                            field.onChange(value);
                            const selected = parsePlenums(value);
                            form.setValue("Plenums", selected);
                            //set index back to first available
                            if (tabIndex != 0) {
                              selectPlenum(selected[0]);
                            }
                          }}

                        />
                      </FormControl>
                    </FormItem>
                  )}
                />


 {selectedPlenums?.length > 0 && tabIndex >= 0 && (
                <div>
                 
                  {selectedPlenums?.length > 1 && (
                    <div className=" flex flex-wrap items-end justify-between gap-4 border-b-2 border-[#ddd]">
                      <div className="tabs !mt-0 flex-1 !border-b-0">
                        {selectedPlenums.map((tab, index) => (
                          <span
                            key={index}
                            className={`tab ${index === tabIndex ? 'active' : ''}`}
                            onClick={() => selectPlenum(tab)}
                          >
                            {plenums.find(p => p.Id === tab)?.Name ?? tab}
                          </span>
                        ))}
                      </div>


                    </div>)}

                  <div>
                    <FormField
                      control={form.control}
                      name={`PresenterSettings.${tabIndex}.ShowLinkInAgenda`}
                      render={({ field }) => (
                        <FormItem >
                          <FormControl>
                            <Checkbox
                              label="If Presenter is listed in Agenda/Speaker overview, show Popup when clicked (see Settings below)"
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                              name={field.name}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              className="mt-3"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name={`PresenterSettings.${tabIndex}.IsGuest`}
                      render={({ field }) => (
                        <FormItem >
                          <FormControl>
                            <Checkbox
                              label="Guest Speaker (only for jitsi and v-transport, NO WEBCALL: use 'camera call' without camera instead)
"
                              checked={!!field.value}
                              onCheckedChange={field.onChange}
                              name={field.name}
                              onBlur={field.onBlur}
                              ref={field.ref}
                              className=""
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                  </div>
                  <div className="mt-4 flex  gap-4 ">
                    <div className="bg-gray-100 p-2 rounded-md w-max flex-1">
                    <div>Presenter Type</div>

                    <table className="w-full  ">
                      <tr>
                        <td>
                          <FormLabel>Module Type</FormLabel>

                        </td>
                        <td>
                          <FormField
                            control={form.control}
                            name={`PresenterSettings.${tabIndex}.SelectedModuleId`}
                            render={({ field }) => (
                              <FormItem className="col-span-2">
                                <FormControl>
                                  <Dropdown
                                    {...field}
                                    value={field.value == null ? field.value : Number(field.value)}
                                    source={presenterTypes}
                                    getLabel={(x) => x.type_name}
                                    getValue={(x) => x.ConnTypeId}
                                    onChange={(value) => {
                                      //field.onChange(value); 
                                      loadModuleSettings(Number(value), 2, tabIndex);
                                    }}
                                  />
                                </FormControl>
                              </FormItem>

                            )}
                          /></td>
                      </tr>

                      {moduleIndex >= 0 && form.getValues(`PresenterSettings.${tabIndex}.PresenterConnections.${moduleIndex}.PresenterConnProperties`)?.map((property, propertyIndex) => (
                        <tr key={`${propertyIndex}_${moduleIndex}`}>
                          <td>
                            <FormLabel>{property.prop_name}</FormLabel>

                          </td>
                          <td>

                            <FormField
                              control={form.control}
                              name={`PresenterSettings.${tabIndex}.PresenterConnections.${moduleIndex}.PresenterConnProperties.${propertyIndex}.prop_value`}
                              render={({ field }) => (
                                <FormItem className="col-span-2">
                                  <FormControl>
                                    {property.prop_type == "SelectBox" ? (
                                      (() => {
                                        const source = getPropertySource(property.prop_id ?? 0);
                                        // const fieldValue = field.value;
                                        // console.log(`Rendering Dropdown for property [${property.prop_name}] for data:`, form.getValues(`PresenterSettings.${tabIndex}`), moduleIndex, propertyIndex);
                                        // console.log(`Dropdown [${property.prop_name}] prop_id=${property.prop_id}:`, {
                                        //   fieldValue,
                                        //   sourceLength: source.length,
                                        //   source,
                                        //   hasMatch: source.some(s => s.select_value === fieldValue)
                                        // });
                                        return (
                                          <Dropdown
                                            {...field}
                                            source={source}
                                            getLabel={(x) => x.select_value}
                                            getValue={(x) => x.select_value}
                                            onChange={(value) => {
                                              field.onChange(value);
                                            }}
                                          />
                                        );
                                      })()
                                    ) : ((property.prop_type == "CheckBox") ? (
                                      <Checkbox
                                        label=""
                                        checked={!!field.value}
                                        onCheckedChange={field.onChange}
                                        name={field.name}
                                        onBlur={field.onBlur}
                                        ref={field.ref}
                                        className="mt-6"
                                      />
                                    ) : (
                                      <Input
                                        {...field}
                                        value={field.value ?? ""}
                                      />
                                    ))}
                                  </FormControl>
                                </FormItem>

                              )}
                            />


                          </td>
                        </tr>
                      ))}
                    </table>
                    </div>

                      <div  className="bg-gray-100 p-2 rounded-md w-max flex-1">
                    <div>Clicker Type</div>

                    <table className="w-full  ">
                      <tr>
                        <td>
                          <FormLabel>Clicker Type</FormLabel>

                        </td>
                        <td>
                          <FormField
                            control={form.control}
                            name={`PresenterSettings.${tabIndex}.SelectedClickerId`}
                            render={({ field }) => (
                              <FormItem className="col-span-2">
                                <FormControl>
                                  <Dropdown
                                    {...field}
                                    value={field.value == null ? field.value : Number(field.value)}
                                    source={clickerTypes}
                                    getLabel={(x) => x.type_name}
                                    getValue={(x) => x.ConnTypeId}
                                    onChange={(value) => {
                                      //field.onChange(value); 
                                      loadModuleSettings(Number(value), 3, tabIndex);
                                    }}
                                  />
                                </FormControl>
                              </FormItem>

                            )}
                          /></td>
                      </tr>

                      {clickerIndex >= 0 && form.getValues(`PresenterSettings.${tabIndex}.PresenterConnections.${clickerIndex}.PresenterConnProperties`)?.map((property, propertyIndex) => (
                        <tr key={`${propertyIndex}_${clickerIndex}`}>
                          <td>
                            <FormLabel>{property.prop_name}</FormLabel>

                          </td>
                          <td>

                            <FormField
                              control={form.control}
                              name={`PresenterSettings.${tabIndex}.PresenterConnections.${clickerIndex}.PresenterConnProperties.${propertyIndex}.prop_value`}
                              render={({ field }) => (
                                <FormItem className="col-span-2">
                                  <FormControl>
                                    {property.prop_type == "SelectBox" ? (
                                      (() => {
                                        const source = getPropertySource(property.prop_id ?? 0);
                                        // const fieldValue = field.value;
                                        // console.log(`Rendering Dropdown for property [${property.prop_name}] for data:`, form.getValues(`PresenterSettings.${tabIndex}`), moduleIndex, propertyIndex);
                                        // console.log(`Dropdown [${property.prop_name}] prop_id=${property.prop_id}:`, {
                                        //   fieldValue,
                                        //   sourceLength: source.length,
                                        //   source,
                                        //   hasMatch: source.some(s => s.select_value === fieldValue)
                                        // });
                                        return (
                                          <Dropdown
                                            {...field}
                                            source={source}
                                            getLabel={(x) => x.select_value}
                                            getValue={(x) => x.select_value}
                                            onChange={(value) => {
                                              field.onChange(value);
                                            }}
                                          />
                                        );
                                      })()
                                    ) : ((property.prop_type == "CheckBox") ? (
                                      <Checkbox
                                        label=""
                                        checked={!!field.value}
                                        onCheckedChange={field.onChange}
                                        name={field.name}
                                        onBlur={field.onBlur}
                                        ref={field.ref}
                                        className="mt-6"
                                      />
                                    ) : (
                                      <Input
                                        {...field}
                                        value={field.value ?? ""}
                                      />
                                    ))}
                                  </FormControl>
                                </FormItem>

                              )}
                            />


                          </td>
                        </tr>
                      ))}

                      {clickerIndex >= 0 && (
                        <tr>
                            <FormLabel className='mt-2'>Mobile Clicker Link</FormLabel>
                          <td><a target="_blank" href={`${form.getValues(`PresenterSettings.${tabIndex}.MobileClickerLink`)}`}>Link</a></td>
                        </tr>
                      )}
                    </table>
                    </div>
                  </div>
                </div>
              )}


              <div className="mt-2 mb-2 border-b-2 border-[#d22]">Settings for Presenter Popup in Agenda or Speaker Overview</div>
<div className="flex gap-6 items-center">


              <div className="space-y-2 min-w-[100px]">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploadingFile}
                  className="relative block w-full max-w-xl overflow-hidden rounded-md focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-70"
                >

                  <img
                    src={`${API_BASE_URL}/protected/presenterPhotos/avatar_${userId}?v=${avatarRefreshToken}`}
                    
                    className="h-30 w-full rounded-md border object-cover cursor-pointer max-w-[150px] max-h-[150px] "
                  />




                  <div className="absolute inset-x-0 bottom-0 flex h-8 items-center justify-center bg-black/45 text-xs text-white">
                    {isUploadingFile ? "Uploading..." : "Click to change image"}
                  </div>
                </button>
              </div>



              <div className="flex-1">
                <div className="flex flex-wrap">
                  {languages.map((language, index) => (
                    <button
                      key={language.key}
                      type="button"
                      className={`rounded-t-md border px-3 py-1 text-sm ${language.key === languageTab ? "!border-blue-500 !bg-blue-50 !text-blue-700" : "border-gray-300 bg-white text-gray-700"}`}
                      onClick={() => setLanguageTab(language.key)}
                    >
                      {language.key}
                    </button>
                  ))}


                </div>


                <div className="border border-gray-300 rounded-b-md p-2 translate-y-[-1px] pt-4">
                  <FormField
                    key={`presenter-name-${presenterNameIndex}-${activeLanguageKey}`}
                    control={form.control}
                    name={`InfoTexts.${presenterNameIndex}.${activeLanguageKey}`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Presenter Name ({activeLanguageKey}) (Html Possible)</FormLabel>
                        <FormControl>
                          <TextArea {...field} value={field.value ?? ""} />
                        </FormControl>
                      </FormItem>
                    )}
                  />



                  <FormField
                    key={`presenter-description-${presenterDescIndex}-${activeLanguageKey}`}
                    control={form.control}
                    name={`InfoTexts.${presenterDescIndex}.${activeLanguageKey}`}
                    render={({ field }) => (
                      <FormItem className="mt-4">
                        <FormLabel>Presenter Description ({activeLanguageKey}) (Html Possible)</FormLabel>
                        <FormControl>
                          <TextArea {...field} value={field.value ?? ""} />
                        </FormControl>
                      </FormItem>
                    )}
                  />





                </div>

              </div>
</div>
              <div className=" flex flex-wrap items-end gap-2 pb-2 justify-end">


                <FormField
                  control={form.control}
                  name={`UserInfo.ContactEmail`}
                  render={({ field }) => (
                    <FormItem className="flex-1">
                      <FormLabel>Contact Email</FormLabel>
                      <FormControl >
                        <Input {...field} value={field.value ?? ""} />
                      </FormControl>
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name={`UserInfo.SortOrder`}
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="max-w-[150px]">Order Number in Speaker Overview</FormLabel>
                      <FormControl>
                        <NumberInput {...field} value={field.value ?? ""} />
                      </FormControl>
                    </FormItem>
                  )}
                />

 
              </div>
             


              <div className="grid-default">
                <div></div>
                <div className="grid-default">
                  <Button className="w-full" type="button" onClick={() => { console.log("Cancel clicked"); onClose(null); }}>Cancel</Button>

                  <Button className="w-full" type="submit">Save</Button>
                </div>
              </div>
              {(errorText != "") && (
                <div className="text-red-500 mt-2">
                  {errorText}
                </div>
              )
              }
            </form>
          </Form>

        </div>)}




    </div>

  )
}



